
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, ArrowLeft, Plus } from 'lucide-react';
import { useBooks } from '@/hooks/useBooks';
import { useAuthor } from '@/hooks/useAuthors';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/formatters';
import { Badge } from '@/components/ui/badge';
import AddBookDialog from '@/components/admin/AddBookDialog';

const AuthorBooks: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: books, isLoading: booksLoading } = useBooks(id);
  const { data: author, isLoading: authorLoading } = useAuthor(id || '');
  const [searchQuery, setSearchQuery] = useState('');

  const isLoading = booksLoading || authorLoading;

  // Group books by title and combine formats
  const groupedBooks = React.useMemo(() => {
    if (!books) return [];

    const bookMap = new Map();
    
    books.forEach(book => {
      const key = book.title.toLowerCase();
      if (bookMap.has(key)) {
        const existing = bookMap.get(key);
        existing.formats.push({
          format: book.format || 'paperback',
          isbn: book.isbn,
          publishDate: book.publishDate
        });
      } else {
        bookMap.set(key, {
          id: book.id,
          title: book.title,
          author: book.author,
          formats: [{
            format: book.format || 'paperback',
            isbn: book.isbn,
            publishDate: book.publishDate
          }]
        });
      }
    });

    return Array.from(bookMap.values());
  }, [books]);

  // Filter books based on search query
  const filteredBooks = React.useMemo(() => {
    if (!groupedBooks) return [];
    
    if (!searchQuery) return groupedBooks;
    
    const query = searchQuery.toLowerCase();
    
    return groupedBooks.filter(book => 
      book.title.toLowerCase().includes(query) || 
      book.formats.some(format => format.isbn.toLowerCase().includes(query)) ||
      book.author?.name?.toLowerCase().includes(query)
    );
  }, [groupedBooks, searchQuery]);

  if (isLoading) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex justify-center p-8">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </MainLayout>
    );
  }

  if (!author) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex justify-center p-8">
          <p className="text-muted-foreground">Author not found</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout allowedRoles={['admin']}>
      <div className="mb-4">
        <Button
          variant="ghost"
          onClick={() => navigate(`/admin/authors/${id}`)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Author Profile
        </Button>
      </div>
      
      <PageHeader
        title={`${author.name}'s Books`}
        description={`Manage books for this author`}
      />
      
      <div className="mb-6">
        <Card className="bg-card dark:bg-card border-border dark:border-border">
          <CardHeader className="bg-card dark:bg-card">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle className="text-card-foreground dark:text-card-foreground">Books</CardTitle>
              
              <div className="flex w-full md:w-auto items-center gap-2">
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search books..."
                    className="pl-8 bg-background dark:bg-background border-input dark:border-input text-foreground dark:text-foreground"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <AddBookDialog 
                  trigger={
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Book
                    </Button>
                  }
                />
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="bg-card dark:bg-card">
            {filteredBooks.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-border dark:border-border">
                    <TableHead className="w-[250px] text-muted-foreground">Title</TableHead>
                    <TableHead className="text-muted-foreground">Author</TableHead>
                    <TableHead className="text-muted-foreground">Formats & ISBNs</TableHead>
                    <TableHead className="text-muted-foreground">Publication Dates</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBooks.map(book => (
                    <TableRow key={book.id} className="border-border dark:border-border hover:bg-muted/50 dark:hover:bg-muted/50">
                      <TableCell className="font-medium text-foreground dark:text-foreground">{book.title}</TableCell>
                      <TableCell className="text-foreground dark:text-foreground">{book.author?.name || 'Unknown'}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {book.formats.map((format, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize border-border dark:border-border text-foreground dark:text-foreground">
                                {format.format}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {format.isbn}
                              </span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {book.formats.map((format, index) => (
                            <div key={index} className="text-sm text-foreground dark:text-foreground">
                              {formatDate(format.publishDate)}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchQuery ? 'No books found matching your search' : 'No books found for this author'}
                </p>
                {/* <Button 
                  onClick={() => {
                    const triggerElement = document.querySelector("[aria-label='Add Book']");
                    if (triggerElement) {
                      (triggerElement as HTMLButtonElement).click();
                    }
                  }}
                  variant="outline"
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Book
                </Button> */}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AuthorBooks;
